/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { BubbleMenu } from "@tiptap/react/menus"
import { DOMSerializer } from "prosemirror-model"
import { CellSelection } from "prosemirror-tables"
import { useTranslation } from "react-i18next"

import type { AiEditHighlightMeta, AiEditHighlightState } from "../tiptap/ai-edit-highlight"
import type { BubbleMenuPluginProps } from "@tiptap/extension-bubble-menu"
import type { Editor } from "@tiptap/react"

import { aiAssistDocumentApi, type DocumentAiAssistTaskMode } from "@/api/api"
import { Loader2, Sparkles } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { DiffPreviewDialog } from "@/components/text-enhancement/DiffPreviewDialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SmallLogo } from "@/customer/small-logo"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { useDocumentAiAssistRouteId } from "../hooks/use-document-ai-assist-route"
import {
  aiEditHighlightKey,
  isSelectionOverlappingAnySuggestion,
} from "../tiptap/ai-edit-highlight"
import { aiSelectionHighlightKey } from "../tiptap/ai-selection-highlight"
import { markdownToTiptapHtml, markdownToTiptapHtmlInline } from "../tiptap/markdown-to-tiptap-html"
import { documentAiAssistRouteFields } from "../utils/ai-assist-route-utils"
import { createCanvasTurndownService } from "../utils/canvas-turndown"
import {
  applyCellReplacements,
  normalizeInsertedMarkdown,
  parseResponseIntoCellValues,
  shouldInsertMarkdownAsBlock,
} from "../utils/selection-edit-utils"

export type PendingSuggestion = {
  id: string
  originalHtml: string
  originalText: string
  modifiedText: string
  from: number
  to: number
  cellRanges?: Array<{ from: number; to: number }>
  originalCellTexts?: string[]
}

type AiEditBubbleProps = {
  editor: Editor | null
  documentId?: string | null
  documentContent?: string
  sessionId?: string | null
  modelId?: string | null
  onSuggestionCreated?: (suggestion: PendingSuggestion) => void
}

type StoredSelection = {
  from: number
  to: number
  text: string
  cellRanges?: Array<{ from: number; to: number }>
}

type DocumentRange = {
  from: number
  to: number
}

const TOP_PLACEMENT_MIN_SPACE_PX = 196
const BOTTOM_PLACEMENT_MIN_SPACE_PX = 140
const MENU_PLACEMENT_PADDING_PX = 24

type PreviewSuggestion = {
  mode: DocumentAiAssistTaskMode
  instruction: string
  explanation?: string
  modifiedText?: string
  originalHtml: string
  originalMarkdown: string
  isInsertMode: boolean
  isMultiCellSelection: boolean
  from: number
  to: number
  editFrom: number
  editTo: number
  cellRanges?: Array<{ from: number; to: number }>
  originalCellTexts: string[]
}

function generateSuggestionId(): string {
  return `suggestion-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function detectTaskMode(instruction: string): DocumentAiAssistTaskMode {
  const normalized = instruction.toLowerCase()
  const explainKeywords = [
    "explain",
    "what does",
    "what is",
    "summarize",
    "why",
    "how",
    "clarify",
    "meaning",
  ]
  const editKeywords = [
    "edit",
    "rewrite",
    "rephrase",
    "fix",
    "improve",
    "change",
    "replace",
    "update",
    "shorten",
    "lengthen",
    "polish",
    "translate",
    "add",
    "remove",
  ]

  if (editKeywords.some((keyword) => normalized.includes(keyword))) return "edit"
  if (explainKeywords.some((keyword) => normalized.includes(keyword))) return "explain"
  return "edit"
}

function selectionContainsList(editor: Editor, from: number, to: number): boolean {
  const slice = editor.state.doc.slice(from, to)
  let foundList = false

  slice.content.descendants((node) => {
    if (node.type.name === "orderedList" || node.type.name === "bulletList") {
      foundList = true
      return false
    }
    return !foundList
  })

  return foundList
}

function getListItemDepths(resolvedPos: ReturnType<Editor["state"]["doc"]["resolve"]>): number[] {
  const depths: number[] = []
  for (let depth = resolvedPos.depth; depth > 0; depth -= 1) {
    if (resolvedPos.node(depth).type.name === "listItem") {
      depths.push(depth)
    }
  }
  return depths
}

function getListItemContentRange(editor: Editor, from: number, to: number): DocumentRange | null {
  const { doc } = editor.state
  const $from = doc.resolve(from)
  const $to = doc.resolve(to)

  const fromDepths = getListItemDepths($from)
  const toDepths = getListItemDepths($to)
  const commonDepths = fromDepths.filter((depth) => toDepths.includes(depth))
  const commonDepth = commonDepths.length > 0 ? Math.max(...commonDepths) : null

  if (!commonDepth) return null

  return {
    from: $from.start(commonDepth),
    to: $from.end(commonDepth),
  }
}

function getBubbleBoundaryElement(editor: Editor): HTMLElement | null {
  return editor.view.dom.closest(".overflow-y-auto") as HTMLElement | null
}

function getBubbleVerticalBounds(editor: Editor): { top: number; bottom: number } {
  const editorElement = editor.view.dom
  const boundaryElement = getBubbleBoundaryElement(editor) ?? editorElement
  const boundaryRect = boundaryElement.getBoundingClientRect()
  const viewportTop = 0
  const viewportBottom = window.innerHeight
  const constrainedTop = Math.max(boundaryRect.top, viewportTop)
  const constrainedBottom = Math.min(boundaryRect.bottom, viewportBottom)

  if (constrainedBottom <= constrainedTop) {
    return { top: viewportTop, bottom: viewportBottom }
  }

  return { top: constrainedTop, bottom: constrainedBottom }
}

function getMenuPlacement(
  editor: Editor,
  anchor: { top: number; bottom: number },
  bubbleHeight: number = 0
): "top" | "bottom" {
  const { top: availableTop, bottom: availableBottom } = getBubbleVerticalBounds(editor)

  const spaceAbove = anchor.top - availableTop
  const spaceBelow = availableBottom - anchor.bottom
  const estimatedBubbleHeight =
    bubbleHeight > 0
      ? bubbleHeight
      : Math.max(TOP_PLACEMENT_MIN_SPACE_PX, BOTTOM_PLACEMENT_MIN_SPACE_PX)
  const availableHeight = Math.max(availableBottom - availableTop, 0)
  const requiredSpace = Math.min(estimatedBubbleHeight + MENU_PLACEMENT_PADDING_PX, availableHeight)
  const canFitAbove = spaceAbove >= requiredSpace
  const canFitBelow = spaceBelow >= requiredSpace

  if (canFitAbove && !canFitBelow) {
    return "top"
  }

  if (canFitBelow && !canFitAbove) {
    return "bottom"
  }

  if (spaceAbove < TOP_PLACEMENT_MIN_SPACE_PX && spaceBelow > BOTTOM_PLACEMENT_MIN_SPACE_PX) {
    return "bottom"
  }

  if (spaceAbove <= spaceBelow) {
    return "bottom"
  }

  return "top"
}

export function AiEditBubble({
  editor,
  documentId,
  documentContent,
  sessionId,
  modelId,
  onSuggestionCreated,
}: AiEditBubbleProps) {
  const { t } = useTranslation()
  const routeId = useDocumentAiAssistRouteId()
  const webSearchEnabled = useWebSearchEnabled()
  const [instruction, setInstruction] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [storedSelection, setStoredSelection] = useState<StoredSelection | null>(null)
  const [previewSuggestion, setPreviewSuggestion] = useState<PreviewSuggestion | null>(null)
  const [showDiffDialog, setShowDiffDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [isOverlapping, setIsOverlapping] = useState(false)
  const [menuPlacement, setMenuPlacement] = useState<"top" | "bottom">("top")
  const [expandedBubbleWidth, setExpandedBubbleWidth] = useState(680)
  const storedSelectionRef = useRef<StoredSelection | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const lastAppliedRangeRef = useRef<{ from: number; to: number } | null>(null)
  const positionUpdateTimeoutRef = useRef<number | null>(null)
  const isExpanded = previewSuggestion !== null

  const turndownService = useMemo(() => createCanvasTurndownService(), [])

  useEffect(() => {
    storedSelectionRef.current = storedSelection
  }, [storedSelection])

  useEffect(() => {
    return () => {
      if (positionUpdateTimeoutRef.current !== null) {
        window.clearTimeout(positionUpdateTimeoutRef.current)
      }
    }
  }, [])

  const clearBubble = useCallback(
    (focusEditor: boolean = false) => {
      storedSelectionRef.current = null
      setStoredSelection(null)
      setInstruction("")
      setPreviewSuggestion(null)
      setShowDiffDialog(false)
      setErrorMessage(null)
      if (focusEditor) {
        editor?.commands.focus()
      }
    },
    [editor]
  )

  const resolveBubbleAppendTarget = useCallback(() => {
    if (!editor) return document.body
    return getBubbleBoundaryElement(editor) ?? editor.view.dom.parentElement ?? document.body
  }, [editor])

  const updateMenuPlacement = useCallback(
    (selection: StoredSelection) => {
      if (!editor) return

      const fromCoords = editor.view.coordsAtPos(selection.from)
      const toCoords = editor.view.coordsAtPos(selection.to)
      const anchorTop = Math.min(fromCoords.top, toCoords.top)
      const anchorBottom = Math.max(fromCoords.bottom, toCoords.bottom)
      const bubbleHeight = bubbleRef.current?.getBoundingClientRect().height ?? 0
      const nextPlacement = getMenuPlacement(
        editor,
        { top: anchorTop, bottom: anchorBottom },
        bubbleHeight
      )

      setMenuPlacement((currentPlacement) =>
        currentPlacement === nextPlacement ? currentPlacement : nextPlacement
      )
    },
    [editor]
  )

  const captureCurrentSelection = useCallback(
    (allowCollapsed: boolean) => {
      if (!editor || !editor.isEditable) return false
      const { selection } = editor.state
      let from: number
      let to: number
      let cellRanges: Array<{ from: number; to: number }> | undefined

      if (selection instanceof CellSelection) {
        const ranges = selection.ranges
        from = Math.min(...ranges.map((range) => range.$from.pos))
        to = Math.max(...ranges.map((range) => range.$to.pos))
        // Document order: cell values are matched to cells by index.
        cellRanges = ranges
          .map((range) => ({ from: range.$from.pos, to: range.$to.pos }))
          .sort((a, b) => a.from - b.from)
      } else {
        from = selection.from
        to = selection.to
      }

      if (!allowCollapsed && from === to) return false

      const text = from === to ? "" : editor.state.doc.textBetween(from, to, " ")
      const nextSelection = { from, to, text, cellRanges }
      storedSelectionRef.current = nextSelection
      setStoredSelection(nextSelection)
      setPreviewSuggestion(null)
      setShowDiffDialog(false)
      setErrorMessage(null)

      updateMenuPlacement(nextSelection)
      return true
    },
    [editor, updateMenuPlacement]
  )

  useEffect(() => {
    if (!storedSelection) return

    const handleClickOutside = (event: MouseEvent) => {
      if (showDiffDialog) return
      if (bubbleRef.current && !bubbleRef.current.contains(event.target as Node)) {
        clearBubble(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [clearBubble, showDiffDialog, storedSelection])

  useEffect(() => {
    if (!editor) return

    if (!isInputFocused || !storedSelection) {
      if (lastAppliedRangeRef.current) {
        lastAppliedRangeRef.current = null
        editor.view.dispatch(editor.state.tr.setMeta(aiSelectionHighlightKey, null))
      }
      return
    }

    const range = { from: storedSelection.from, to: storedSelection.to }
    const lastRange = lastAppliedRangeRef.current
    if (lastRange?.from === range.from && lastRange.to === range.to) return

    lastAppliedRangeRef.current = range
    editor.view.dispatch(editor.state.tr.setMeta(aiSelectionHighlightKey, range))
  }, [editor, isInputFocused, storedSelection])

  useEffect(() => {
    if (!editor || !storedSelection) {
      setIsOverlapping(false)
      return
    }

    const highlightState = aiEditHighlightKey.getState(editor.state) as AiEditHighlightState | null
    if (!highlightState || highlightState.size === 0) {
      setIsOverlapping(false)
      return
    }

    const overlaps = isSelectionOverlappingAnySuggestion(
      highlightState,
      storedSelection.from,
      storedSelection.to
    )
    setIsOverlapping(overlaps)
  }, [editor, storedSelection])

  useEffect(() => {
    if (!editor) return

    const handleDoubleClick = () => {
      setTimeout(() => {
        if (!storedSelectionRef.current && captureCurrentSelection(true)) {
          editor.view.dispatch(editor.state.tr)
        }
      }, 0)
    }

    editor.view.dom.addEventListener("dblclick", handleDoubleClick)
    return () => editor.view.dom.removeEventListener("dblclick", handleDoubleClick)
  }, [captureCurrentSelection, editor])

  useEffect(() => {
    if (!editor) return

    const handleOpenBubble = () => {
      const opened = captureCurrentSelection(true)
      if (!opened) return
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    }

    window.addEventListener("canvas:open-ai-edit-bubble", handleOpenBubble as EventListener)
    return () =>
      window.removeEventListener("canvas:open-ai-edit-bubble", handleOpenBubble as EventListener)
  }, [captureCurrentSelection, editor])

  const applyPreviewSuggestion = useCallback(
    (preview: PreviewSuggestion) => {
      if (!editor || !preview.modifiedText?.trim()) return

      const normalizedInsertedMarkdown = normalizeInsertedMarkdown(preview.modifiedText)

      if (preview.isMultiCellSelection && preview.cellRanges && preview.cellRanges.length > 0) {
        const cellValues = parseResponseIntoCellValues(
          normalizedInsertedMarkdown,
          preview.cellRanges.length,
          preview.originalCellTexts
        )
        const newCellRanges = applyCellReplacements(editor, preview.cellRanges, cellValues)
        const suggestionId = generateSuggestionId()
        const meta: AiEditHighlightMeta = {
          action: "add",
          id: suggestionId,
          ranges: newCellRanges,
        }
        editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

        const highlightFrom = Math.min(...newCellRanges.map((range) => range.from))
        const highlightTo = Math.max(...newCellRanges.map((range) => range.to))
        onSuggestionCreated?.({
          id: suggestionId,
          originalHtml: preview.originalHtml,
          originalText: preview.originalMarkdown,
          modifiedText: preview.modifiedText,
          from: highlightFrom,
          to: highlightTo,
          cellRanges: newCellRanges,
          originalCellTexts: preview.originalCellTexts,
        })
      } else {
        const insertedHtml = shouldInsertMarkdownAsBlock(normalizedInsertedMarkdown)
          ? markdownToTiptapHtml(normalizedInsertedMarkdown)
          : markdownToTiptapHtmlInline(normalizedInsertedMarkdown)

        if (preview.isInsertMode) {
          editor.chain().focus().insertContentAt(preview.from, insertedHtml).run()
        } else {
          editor
            .chain()
            .focus()
            .deleteRange({ from: preview.editFrom, to: preview.editTo })
            .insertContentAt(preview.editFrom, insertedHtml)
            .run()
        }

        const selectionAfter = editor.state.selection
        const endPos = selectionAfter.to
        const startPos = preview.isInsertMode ? preview.from : preview.editFrom
        if (startPos !== endPos) {
          const suggestionId = generateSuggestionId()
          const highlightRange = {
            from: Math.min(startPos, endPos),
            to: Math.max(startPos, endPos),
          }
          const meta: AiEditHighlightMeta = {
            action: "add",
            id: suggestionId,
            ranges: [highlightRange],
          }
          editor.view.dispatch(editor.state.tr.setMeta(aiEditHighlightKey, meta))

          onSuggestionCreated?.({
            id: suggestionId,
            originalHtml: preview.originalHtml,
            originalText: preview.originalMarkdown,
            modifiedText: preview.modifiedText,
            from: highlightRange.from,
            to: highlightRange.to,
          })
        }
      }

      clearBubble(true)
    },
    [clearBubble, editor, onSuggestionCreated]
  )

  const handleSubmit = useCallback(async () => {
    if (!editor || !documentId || !instruction.trim() || isLoading || isOverlapping) return

    const selection = storedSelection
    if (!selection) return

    const isInsertMode = selection.from === selection.to
    if (!isInsertMode && !selection.text.trim()) return

    const { from, to, cellRanges } = selection
    const listItemRange =
      !isInsertMode && selectionContainsList(editor, from, to)
        ? getListItemContentRange(editor, from, to)
        : null
    const editFrom = listItemRange?.from ?? from
    const editTo = listItemRange?.to ?? to

    const highlightState = aiEditHighlightKey.getState(editor.state) as AiEditHighlightState | null
    if (highlightState && isSelectionOverlappingAnySuggestion(highlightState, editFrom, editTo)) {
      return
    }

    const isMultiCellSelection = Boolean(cellRanges && cellRanges.length > 1)
    let originalHtml: string
    let originalMarkdown: string
    let originalCellTexts: string[] = []

    if (isInsertMode) {
      originalHtml = ""
      originalMarkdown = ""
    } else if (isMultiCellSelection && cellRanges) {
      originalCellTexts = cellRanges.map((cellRange) =>
        editor.state.doc.textBetween(cellRange.from, cellRange.to, "")
      )
      originalMarkdown = originalCellTexts.join("\n")
      originalHtml = originalCellTexts.map((text) => `<p>${text}</p>`).join("")
    } else {
      const originalSlice = editor.state.doc.slice(editFrom, editTo)
      const serializer = DOMSerializer.fromSchema(editor.schema)
      const fragment = serializer.serializeFragment(originalSlice.content)
      const tempDiv = document.createElement("div")
      tempDiv.appendChild(fragment)
      originalHtml = tempDiv.innerHTML
      originalMarkdown = turndownService.turndown(originalHtml)
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const settingsService = SettingsService.getInstance()
      const taskMode = detectTaskMode(instruction.trim())
      const response = await aiAssistDocumentApi(
        documentId,
        {
          instruction: instruction.trim(),
          task_mode: taskMode,
          selected_text: originalMarkdown,
          selection_start: editFrom,
          selection_end: editTo,
          document_content_snippet: documentContent?.slice(0, 4000),
          model_id: modelId ?? undefined,
          session_id: sessionId ?? undefined,
          ...documentAiAssistRouteFields(routeId, webSearchEnabled),
        },
        settingsService.token
      )

      if (!response.success) {
        throw new Error(response.error || t("canvas.ai_failed", { defaultValue: "AI Edit Failed" }))
      }

      const modifiedText =
        typeof response.edited_text === "string" && response.edited_text.trim()
          ? response.edited_text
          : undefined
      const explanation =
        typeof response.explanation === "string" && response.explanation.trim()
          ? response.explanation
          : undefined

      if (response.task_mode === "edit" && !modifiedText) {
        throw new Error(
          t("canvas.ai_no_edit_result", {
            defaultValue: "The assistant did not return an editable result.",
          })
        )
      }
      if (!modifiedText && !explanation) {
        throw new Error(
          t("canvas.ai_empty_result", { defaultValue: "The assistant returned an empty response." })
        )
      }

      setPreviewSuggestion({
        mode: response.task_mode,
        instruction: instruction.trim(),
        explanation,
        modifiedText,
        originalHtml,
        originalMarkdown,
        isInsertMode,
        isMultiCellSelection,
        from,
        to,
        editFrom,
        editTo,
        cellRanges,
        originalCellTexts,
      })
      setInstruction("")
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("canvas.ai_failed", { defaultValue: "AI Edit Failed" })
      )
    } finally {
      setIsLoading(false)
    }
  }, [
    documentContent,
    documentId,
    editor,
    instruction,
    isLoading,
    isOverlapping,
    modelId,
    routeId,
    sessionId,
    storedSelection,
    t,
    turndownService,
    webSearchEnabled,
  ])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        void handleSubmit()
      }
      if (event.key === "Escape") {
        clearBubble(true)
      }
    },
    [clearBubble, handleSubmit]
  )

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true)
  }, [])

  const handleInputBlur = useCallback(() => {
    setIsInputFocused(false)
  }, [])

  const shouldShow = useCallback<NonNullable<BubbleMenuPluginProps["shouldShow"]>>(
    ({ editor: tiptapEditor, from, to }) => {
      if (storedSelectionRef.current) return true
      if (!tiptapEditor.isEditable) return false

      if (tiptapEditor.state.selection instanceof CellSelection) {
        return tiptapEditor.state.selection.ranges.length > 0
      }

      return from !== to
    },
    []
  )

  const handleShow = useCallback(() => {
    captureCurrentSelection(false)
    requestAnimationFrame(() => {
      editor?.commands.setMeta("bubbleMenu", "updatePosition")
    })

    if (positionUpdateTimeoutRef.current !== null) {
      window.clearTimeout(positionUpdateTimeoutRef.current)
    }

    positionUpdateTimeoutRef.current = window.setTimeout(() => {
      editor?.commands.setMeta("bubbleMenu", "updatePosition")
      positionUpdateTimeoutRef.current = null
    }, 320)
  }, [captureCurrentSelection, editor])

  useEffect(() => {
    if (!storedSelection) return

    const rafId = requestAnimationFrame(() => {
      updateMenuPlacement(storedSelection)
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [isExpanded, storedSelection, updateMenuPlacement])

  useEffect(() => {
    if (!editor || !isExpanded || !storedSelection) return

    const updateExpandedBubbleWidth = () => {
      const boundaryElement = getBubbleBoundaryElement(editor) ?? editor.view.dom
      const boundaryWidth = boundaryElement.getBoundingClientRect().width
      const nextWidth = Math.min(680, Math.max(320, boundaryWidth - MENU_PLACEMENT_PADDING_PX * 2))

      setExpandedBubbleWidth((currentWidth) => {
        if (Math.abs(currentWidth - nextWidth) < 1) return currentWidth
        return nextWidth
      })
    }

    updateExpandedBubbleWidth()
    window.addEventListener("resize", updateExpandedBubbleWidth)

    return () => {
      window.removeEventListener("resize", updateExpandedBubbleWidth)
    }
  }, [editor, isExpanded, storedSelection])

  useEffect(() => {
    if (!editor || !storedSelection) return

    const rafId = requestAnimationFrame(() => {
      const bubbleRect = bubbleRef.current?.getBoundingClientRect()
      if (!bubbleRect) return

      const { top: availableTop, bottom: availableBottom } = getBubbleVerticalBounds(editor)
      const overflowsTop = bubbleRect.top < availableTop + MENU_PLACEMENT_PADDING_PX
      const overflowsBottom = bubbleRect.bottom > availableBottom - MENU_PLACEMENT_PADDING_PX

      if (overflowsBottom && !overflowsTop && menuPlacement !== "top") {
        setMenuPlacement("top")
      } else if (overflowsTop && !overflowsBottom && menuPlacement !== "bottom") {
        setMenuPlacement("bottom")
      }
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [editor, isExpanded, menuPlacement, storedSelection])

  useEffect(() => {
    if (!editor || !storedSelection) return

    const rafId = requestAnimationFrame(() => {
      editor.commands.setMeta("bubbleMenu", "updatePosition")
    })

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [editor, menuPlacement, storedSelection])

  const bubbleMenuOptions = useMemo(() => {
    const boundaryElement = editor ? getBubbleBoundaryElement(editor) : null

    return {
      placement: menuPlacement,
      offset: 32,
      flip: boundaryElement
        ? { padding: MENU_PLACEMENT_PADDING_PX, boundary: boundaryElement }
        : { padding: MENU_PLACEMENT_PADDING_PX },
      shift: boundaryElement
        ? { padding: MENU_PLACEMENT_PADDING_PX, boundary: boundaryElement, crossAxis: true }
        : { padding: MENU_PLACEMENT_PADDING_PX, crossAxis: true },
      zIndex: 9999,
      onShow: handleShow,
    }
  }, [editor, handleShow, menuPlacement])

  const placeholderText = useMemo(() => {
    if (isOverlapping) {
      return t("canvas.ai_edit_overlap_warning", "Selection overlaps pending suggestion")
    }
    if (!documentId) {
      return t("canvas.ai_edit_requires_document", "Open a document to use AI edit")
    }
    if (storedSelection?.from === storedSelection?.to) {
      return t("canvas.ai_insert_placeholder", "Edit / explain this text...")
    }
    return t("canvas.ai_edit_placeholder", "Edit / explain this text...")
  }, [documentId, isOverlapping, storedSelection, t])

  const handleDiscardPreview = useCallback(() => {
    setPreviewSuggestion(null)
    setShowDiffDialog(false)
  }, [])

  const handleApplyPreview = useCallback(() => {
    if (!previewSuggestion) return
    applyPreviewSuggestion(previewSuggestion)
  }, [applyPreviewSuggestion, previewSuggestion])

  if (!editor) return null

  return (
    <>
      <BubbleMenu
        appendTo={resolveBubbleAppendTarget}
        editor={editor}
        options={bubbleMenuOptions}
        shouldShow={shouldShow}
        style={isExpanded ? { width: expandedBubbleWidth } : undefined}
        className={cn(
          "bg-background/95 z-[2147483647] border shadow-lg backdrop-blur",
          isExpanded ? "rounded-lg px-3 py-3" : "flex items-center gap-2 rounded-full px-3 py-2"
        )}
      >
        <div
          ref={bubbleRef}
          className={cn("flex gap-2", isExpanded ? "w-full flex-col" : "items-center")}
        >
          <div className="flex items-center gap-2">
            <div className="flex size-8 shrink-0 items-center justify-center">
              <SmallLogo className="size-6" />
            </div>
            <Input
              ref={inputRef}
              disabled={isLoading}
              placeholder={placeholderText}
              value={instruction}
              className={cn(
                "border-0 bg-transparent px-2 text-sm focus-visible:ring-0",
                isExpanded ? "h-9 w-full" : "h-8 w-64"
              )}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              onChange={(event) => {
                setInstruction(event.target.value)
                setErrorMessage(null)
              }}
            />
            <Button
              className="size-8 shrink-0 rounded-full"
              disabled={!instruction.trim() || isLoading || isOverlapping || !documentId}
              size="icon"
              variant="ghost"
              onClick={handleSubmit}
            >
              {isLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Sparkles className="size-4" />
              )}
            </Button>
          </div>

          {errorMessage && <p className="text-destructive px-1 text-xs">{errorMessage}</p>}

          {previewSuggestion && (
            <div className="flex flex-col gap-3 border-t pt-3">
              {previewSuggestion.explanation && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {t("canvas.ai_explanation_label", { defaultValue: "Explanation" })}
                  </p>
                  <div className="bg-muted/35 max-h-48 overflow-y-auto rounded-md border px-3 py-2 text-sm">
                    <MarkdownRenderer content={previewSuggestion.explanation} />
                  </div>
                </div>
              )}

              {previewSuggestion.modifiedText && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    {previewSuggestion.mode === "edit"
                      ? t("canvas.ai_proposed_edit", { defaultValue: "Proposed edit" })
                      : t("canvas.ai_optional_edit", { defaultValue: "Optional edit proposal" })}
                  </p>
                  <div className="bg-muted/35 max-h-56 overflow-y-auto rounded-md border px-3 py-2 text-sm">
                    <MarkdownRenderer content={previewSuggestion.modifiedText} />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                {previewSuggestion.modifiedText && (
                  <Button size="sm" variant="outline" onClick={() => setShowDiffDialog(true)}>
                    {t("canvas.ai_view_diff", { defaultValue: "View diff" })}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={handleDiscardPreview}>
                  {t("canvas.discard", { defaultValue: "Discard" })}
                </Button>
                {previewSuggestion.modifiedText && (
                  <Button size="sm" onClick={handleApplyPreview}>
                    {t("canvas.apply", { defaultValue: "Apply" })}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </BubbleMenu>

      {previewSuggestion?.modifiedText && (
        <DiffPreviewDialog
          modified={previewSuggestion.modifiedText}
          open={showDiffDialog}
          operationName={t("canvas.ai_edit_operation", { defaultValue: "AI edit" })}
          original={previewSuggestion.originalMarkdown}
          onAccept={handleApplyPreview}
          onOpenChange={setShowDiffDialog}
          onReject={() => setShowDiffDialog(false)}
        />
      )}
    </>
  )
}
